import type { DataDrivenPropertyValueSpecification } from 'mapbox-gl';

import { LocationType } from '../types';
import {
  BorderColor,
  NeutralColor,
  NoDataFillColor,
} from '../utils/color-scale';
import type { ChoroplethLayerConfig } from './ChoroplethConfig';

function buildMatchExpr(
  propertyName: string,
  data: Record<string, number>,
  colorScale: (value: number) => string,
  fallbackColor: string
): DataDrivenPropertyValueSpecification<string> {
  const expr: DataDrivenPropertyValueSpecification<string> = [
    'match',
    ['to-string', ['get', propertyName]],
  ];
  Object.entries(data).forEach(([name, val]) => {
    if (!name || name === '' || val === undefined || val === null) return;
    expr.push(name, colorScale(val) ?? fallbackColor);
  });
  expr.push(fallbackColor);
  return expr;
}

export type FillAndOutlineExpr = {
  fillColorExpr: DataDrivenPropertyValueSpecification<string> | string;
  outlineColorExpr: DataDrivenPropertyValueSpecification<string> | string;
};

export function buildFillAndOutline(
  config: ChoroplethLayerConfig,
  data: Record<string, number>,
  colorScale: (value: number) => string,
  hasNonMetroData: boolean,
  options?: { extraNonDataMsaIds?: string[] }
): FillAndOutlineExpr {
  const isMetro = config.id === LocationType.METRO_AREA;
  const extraNonDataMsaIds: string[] = options?.extraNonDataMsaIds || [];

  if (Object.keys(data).length === 0) {
    if (isMetro) {
      const fill = NeutralColor;
      const outline = BorderColor;
      return { fillColorExpr: fill, outlineColorExpr: outline };
    }

    return {
      fillColorExpr: NoDataFillColor,
      outlineColorExpr: BorderColor,
    };
  }

  if (isMetro) {
    const colorMatch: DataDrivenPropertyValueSpecification<string> = [
      'match',
      ['to-string', ['get', config.propertyName]],
    ];
    const outlineMatch: DataDrivenPropertyValueSpecification<string> = [
      'match',
      ['to-string', ['get', config.propertyName]],
    ];

    Object.entries(data).forEach(([name, val]) => {
      if (!name || name === '' || val === undefined || val === null) return;
      const color = colorScale(val) ?? NoDataFillColor;
      colorMatch.push(name, color);
      outlineMatch.push(name, BorderColor);
    });

    const fallbackFill = NeutralColor;

    const fallbackOutline: DataDrivenPropertyValueSpecification<string> = [
      'case',
      inListExpr('msa_id', extraNonDataMsaIds),
      BorderColor,
      BorderColor,
    ];

    colorMatch.push(fallbackFill);
    outlineMatch.push(fallbackOutline);

    return { fillColorExpr: colorMatch, outlineColorExpr: outlineMatch };
  }

  const fallbackFill = NoDataFillColor;
  const fallbackOutline = BorderColor;

  const fillColorExpr = buildMatchExpr(
    config.propertyName,
    data,
    colorScale,
    fallbackFill
  );

  const outlineColorExpr: DataDrivenPropertyValueSpecification<string> = [
    'match',
    ['to-string', ['get', config.propertyName]],
    ...Object.keys(data).flatMap((name) => [name, BorderColor]),
    fallbackOutline,
  ];

  return { fillColorExpr, outlineColorExpr };
}

function inListExpr(
  property: string,
  values: string[]
): DataDrivenPropertyValueSpecification<boolean> {
  return ['in', ['to-string', ['get', property]], ['literal', values]];
}
